<template>
  <div class="section-hobbies">
    <div v-if="page" v-editable="page" class="container-page">
      <header>
        <BaseHeading primary :label="$t('hobbies.title')">
          <template v-if="page.full_slug" #breadcrumbs>
            <BaseBreadcrumbs :items="breadcrumbs" />
          </template>
          {{ page.content.title }}
        </BaseHeading>
      </header>
      <StoryblokComponent
        v-for="blok in bloks"
        :key="blok._uid"
        :blok="blok"
        class="section-hobbies__content"
      />
      <EndOfPage />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ISbStoryData } from '@storyblok/vue'

import { useLocalizedStoryParams } from '@/composables/useLocalizedStoryParams'

const localePath = useLocalePath()

defineOptions({
  name: 'DynamicHobbyPage',
})

const { params } = useRoute()
const storyblokApi = useStoryblokApi()
const { $getMetadataFromStory, $setMetadata } = useNuxtApp()

const { getParams } = useLocalizedStoryParams()
const { t } = useI18n()

const page = ref<ISbStoryData | undefined>()

const slugParams = computed(() => {
  return [params.slug].flat().join('/')
})

const { data } = await useAsyncData(`hobby-${slugParams.value}`, () =>
  storyblokApi
    .get(`cdn/stories/hobbies/${slugParams.value}`, {
      ...getParams(),
      resolve_relations: 'HobbiesList.items',
    })
    .then((response) => response.data)
    .catch(() => null),
)

if (data.value) {
  page.value = data.value.story
}

if (!page.value) {
  throw createError({
    statusCode: 404,
  })
}

const content = computed(() => page.value?.content)
const breadcrumbs = computed(() => [
  {
    slug: localePath({ name: 'hobbies' }),
    title: t('hobbies.title'),
  },
])

const bloks = computed(() => {
  if (!content.value?.body) {
    return []
  }
  return content.value.body.map((item) => {
    if (item.component === 'HobbiesList') {
      return {
        ...item,
        items: Array.isArray(item.items)
          ? item.items.map((story) => ({ ...story.content, full_slug: story.full_slug }))
          : [],
      }
    }

    return item
  })
})

onMounted(() => {
  if (page.value?.id) {
    useStoryblokBridge(page.value.id, (updatedStory) => (page.value = updatedStory))
  }
})

if (content.value) {
  $setMetadata($getMetadataFromStory(content.value))
}
</script>

<style lang="scss">
.section-hobbies {
  &__content {
    .long-text__content {
      font-family: $font-family-title;
      line-height: 1.8;
      font-size: 1.2em;
      font-weight: 200;
      max-width: 1000px;
    }
  }
}
</style>
